import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_URL, formatError } from "../lib/api.js";
import {
  Ship, PackageSearch, Warehouse, ParkingCircle, Wrench, CheckCircle2,
  History, LayoutDashboard, Plus, Filter, X, Clock, AlertTriangle,
  MapPin, Loader2, ArrowRight, Search as SearchIcon, Download, Bell, Settings,
} from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

// ─────────────────────────────────────────────────────────────────────────────
// Status buckets per tab — matches backend LOGISTICS_STATUSES.
const TABS = [
  { k: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { k: "all",       label: "Todas as Viaturas", Icon: Ship },
  { k: "transit",   label: "Trânsito",       Icon: Ship,     statuses: ["Em produção","Em trânsito internacional","Chegada ao porto","Descarga concluída","Transferência para W7"] },
  { k: "w7",        label: "Porto / W7 / Alfândega", Icon: Warehouse, statuses: ["Em W7 – Direitos pendentes","Processo aduaneiro em curso","Direitos regularizados","Libertada do W7","Em transporte local"] },
  { k: "park",      label: "Parque",         Icon: ParkingCircle, statuses: ["Recebida no parque Ronil","Em inspecção"] },
  { k: "pdi",       label: "PDI & Preparação", Icon: Wrench, statuses: ["A aguardar PDI","PDI em curso","A aguardar acessórios","A aguardar resolução de danos"] },
  { k: "ready",     label: "Prontas para VR", Icon: CheckCircle2, statuses: ["Pronta","Pronta para VR"] },
  { k: "history",   label: "Histórico",      Icon: History,  isActive: false },
];

const AGE_LABELS = { "0_5": "0-5 dias", "6_10": "6-10 dias", "11_20": "11-20 dias", "21_30": "21-30 dias", "over_30": ">30 dias" };

export default function LogisticsPage() {
  const [tab, setTab] = useState("dashboard");
  const [enums, setEnums] = useState(null);
  const [rows, setRows] = useState(null);
  const [dash, setDash] = useState(null);
  const [filters, setFilters] = useState({ q: "", status: "", location_kind: "", responsible_department: "", destination_dealer: "", has_blocker: "" });
  const [showNew, setShowNew] = useState(false);

  useEffect(() => { api.get("/logistics/enums").then((r) => setEnums(r.data)).catch(() => {}); }, []);

  const activeTab = useMemo(() => TABS.find((t) => t.k === tab), [tab]);

  const load = async () => {
    if (tab === "dashboard") {
      try { setDash((await api.get("/vehicle-logistics/dashboard")).data); } catch {}
      return;
    }
    const params = { is_active: activeTab?.isActive === false ? false : true };
    if (filters.q) params.q = filters.q;
    if (filters.status) params.status = filters.status;
    if (filters.location_kind) params.location_kind = filters.location_kind;
    if (filters.responsible_department) params.responsible_department = filters.responsible_department;
    if (filters.destination_dealer) params.destination_dealer = filters.destination_dealer;
    if (filters.has_blocker) params.has_blocker = filters.has_blocker === "yes";
    try {
      let data = (await api.get("/vehicle-logistics", { params })).data || [];
      // Client-side slice by tab's status whitelist
      if (activeTab?.statuses) data = data.filter((d) => activeTab.statuses.includes(d.status));
      setRows(data);
    } catch (e) { setRows([]); }
  };

  useEffect(() => { setRows(null); load(); /* eslint-disable-next-line */ }, [tab, filters]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-screen-2xl mx-auto">
      <PageHeader
        overline="Operacional"
        title="Logística de Viaturas"
        subtitle="Controlo operacional da viatura — da encomenda à transferência para VR. Nenhum valor financeiro é tratado aqui — tudo o que é dinheiro permanece no ERP."
      />

      {/* Top bar: tabs + action */}
      <div className="flex items-center gap-2 flex-wrap mb-4 border-b border-slate-200">
        <div className="flex flex-wrap gap-1">
          {TABS.map((t) => {
            const Icon = t.Icon;
            const active = tab === t.k;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`logistics-tab-${t.k}`}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-heading font-semibold border-b-2 transition-all -mb-px ${active ? "border-ronil-navy text-ronil-navy" : "border-transparent text-slate-500 hover:text-ronil-navy"}`}
              >
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="ml-auto bg-ronil-navy hover:bg-ronil-navy-hover text-white px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm"
          data-testid="new-logistics-btn"
        >
          <Plus className="w-4 h-4" /> Novo processo
        </button>
        <a
          href={`${API_URL}/logistics/export.xlsx?is_active=true`}
          target="_blank" rel="noreferrer"
          className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm"
          data-testid="export-xlsx-btn"
          title="Exportar Excel"
        >
          <Download className="w-4 h-4" /> XLSX
        </a>
        <a
          href={`${API_URL}/logistics/export.csv?is_active=true`}
          target="_blank" rel="noreferrer"
          className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm"
          data-testid="export-csv-btn"
          title="Exportar CSV"
        >
          <Download className="w-4 h-4" /> CSV
        </a>
        <Link to="/logistica/alertas" className="bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm" data-testid="logistics-alerts-link">
          <Bell className="w-4 h-4" /> Alertas
        </Link>
        <Link to="/logistica/configuracoes" className="bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md text-sm font-heading font-semibold inline-flex items-center gap-1.5 shadow-sm" data-testid="logistics-settings-link">
          <Settings className="w-4 h-4" /> Configurações
        </Link>
      </div>

      {tab === "dashboard" ? (
        <Dashboard dash={dash} enums={enums} onDrill={(s) => { setTab("all"); setFilters({ ...filters, status: s }); }} />
      ) : (
        <>
          <FiltersBar filters={filters} setFilters={setFilters} enums={enums} />
          <List rows={rows} />
        </>
      )}

      {showNew && enums && (
        <NewLogisticsDialog enums={enums} onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function Dashboard({ dash, enums, onDrill }) {
  if (!dash) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div>;
  const cards = [
    { label: "Em curso", value: dash.active, tone: "navy" },
    { label: "Workflow incompleto", value: dash.workflow_incomplete, tone: dash.workflow_incomplete ? "rose" : "slate" },
    { label: "Bloqueadas", value: dash.blocked, tone: dash.blocked ? "amber" : "slate" },
    { label: "W7 (em curso)", value: dash.w7.total, tone: "navy" },
    { label: "W7 > 7 dias", value: dash.w7.over_7d, tone: dash.w7.over_7d ? "amber" : "slate" },
    { label: "W7 > 14 dias", value: dash.w7.over_14d, tone: dash.w7.over_14d ? "rose" : "slate" },
    { label: "W7 > 30 dias", value: dash.w7.over_30d, tone: dash.w7.over_30d ? "rose" : "slate" },
    { label: "Prontas para VR", value: dash.ready_for_vr, tone: "emerald" },
    { label: "Transferidas hoje", value: dash.transferred_today, tone: "emerald" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="logistics-dashboard-cards">
        {cards.map((c) => <KPICard key={c.label} {...c} />)}
      </div>

      <section>
        <h3 className="font-heading font-bold text-ronil-navy mb-2 text-sm uppercase tracking-widest">Antiguidade (dias desde abertura)</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(dash.age_bands).map(([k, v]) => <KPICard key={k} label={AGE_LABELS[k]} value={v} tone={k === "over_30" && v ? "rose" : "slate"} />)}
        </div>
      </section>

      <section>
        <h3 className="font-heading font-bold text-ronil-navy mb-2 text-sm uppercase tracking-widest">Por estado</h3>
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr><th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-right">Em curso</th></tr>
            </thead>
            <tbody>
              {(enums?.statuses || []).map((s) => (
                <tr key={s} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => onDrill(s)}>
                  <td className="px-3 py-1.5">{s}</td>
                  <td className="px-3 py-1.5 text-right font-mono tabular-nums">{dash.by_status?.[s] || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

const TONE = { navy: "bg-ronil-navy-50 text-ronil-navy border-slate-200", rose: "bg-rose-50 text-rose-700 border-rose-200", amber: "bg-amber-50 text-amber-700 border-amber-200", emerald: "bg-emerald-50 text-emerald-700 border-emerald-200", slate: "bg-slate-50 text-slate-600 border-slate-200" };
function KPICard({ label, value, tone = "navy" }) {
  return (
    <div className={`rounded-md border px-3 py-2.5 ${TONE[tone]}`}>
      <div className="text-[9px] uppercase tracking-widest font-heading font-semibold opacity-80">{label}</div>
      <div className="font-heading font-bold text-2xl leading-none mt-1 tabular-nums">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function FiltersBar({ filters, setFilters, enums }) {
  const upd = (k, v) => setFilters({ ...filters, [k]: v });
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="logistics-filters">
      <div className="relative flex-1 min-w-[200px]">
        <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-slate-400" />
        <input value={filters.q} onChange={(e) => upd("q", e.target.value)} placeholder="VIN, marca, modelo…" className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded-md focus:border-ronil-navy focus:outline-none" data-testid="logistics-filter-q" />
      </div>
      <select value={filters.status} onChange={(e) => upd("status", e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:border-ronil-navy focus:outline-none" data-testid="logistics-filter-status">
        <option value="">Todos os estados</option>
        {(enums?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.location_kind} onChange={(e) => upd("location_kind", e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:border-ronil-navy focus:outline-none">
        <option value="">Qualquer localização</option>
        {(enums?.location_kinds || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.responsible_department} onChange={(e) => upd("responsible_department", e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:border-ronil-navy focus:outline-none">
        <option value="">Qualquer departamento</option>
        {(enums?.departments || []).map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select value={filters.has_blocker} onChange={(e) => upd("has_blocker", e.target.value)} className="text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:border-ronil-navy focus:outline-none">
        <option value="">Qualquer bloqueio</option>
        <option value="yes">Só com bloqueio</option>
        <option value="no">Sem bloqueios</option>
      </select>
      {Object.values(filters).some(Boolean) && (
        <button onClick={() => setFilters({ q: "", status: "", location_kind: "", responsible_department: "", destination_dealer: "", has_blocker: "" })} className="text-xs text-slate-500 hover:text-ronil-navy inline-flex items-center gap-1 px-2 py-1">
          <X className="w-3 h-3" /> Limpar
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function List({ rows }) {
  if (rows === null) return <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div>;
  if (rows.length === 0) return <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-400">Sem processos para mostrar.</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left">VIN</th>
            <th className="px-3 py-2 text-left">Viatura</th>
            <th className="px-3 py-2 text-left">Estado</th>
            <th className="px-3 py-2 text-left">Localização</th>
            <th className="px-3 py-2 text-left">Responsável</th>
            <th className="px-3 py-2 text-left">Próxima acção</th>
            <th className="px-3 py-2 text-right">Dias (estado · W7 · total)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`logistics-row-${r.id}`}>
              <td className="px-3 py-2 font-mono text-xs text-ronil-navy">
                <Link to={`/vehicles/${r.vin}`} className="hover:underline">{r.vin}</Link>
              </td>
              <td className="px-3 py-2 text-xs">
                {r.vehicle?.brand || "—"} {r.vehicle?.model || ""}
                {r.vehicle?.version && <span className="text-slate-400"> · {r.vehicle.version}</span>}
              </td>
              <td className="px-3 py-2 text-xs">
                <StatusBadge status={r.status} />
                {!r.workflow_complete && <div className="text-[10px] text-rose-600 mt-0.5">Workflow incompleto</div>}
                {r.blocked_reason && <div className="text-[10px] text-amber-600 mt-0.5 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> {r.blocked_reason}</div>}
              </td>
              <td className="px-3 py-2 text-xs">
                {r.physical_location?.kind ? (
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-slate-400" />{r.physical_location.kind}{r.physical_location.zone ? ` · ${r.physical_location.zone}` : ""}</span>
                ) : "—"}
              </td>
              <td className="px-3 py-2 text-xs">{r.responsible_department || "—"}</td>
              <td className="px-3 py-2 text-xs">
                <div>{r.next_action || <span className="text-slate-400">—</span>}</div>
                {r.deadline && <div className="text-[10px] text-slate-500">até {r.deadline}</div>}
              </td>
              <td className="px-3 py-2 text-xs text-right font-mono tabular-nums">
                <span className="text-slate-700">{r.days_in_status ?? 0}</span>
                <span className="text-slate-400"> · {r.days_in_w7 ?? "—"} · {r.days_total ?? 0}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const w7 = status?.includes("W7") || status?.includes("aduaneiro");
  const ready = status?.startsWith("Pronta");
  const transit = status?.includes("trânsito") || status?.includes("Em produção");
  const cls = w7 ? "bg-amber-100 text-amber-800" : ready ? "bg-emerald-100 text-emerald-800" : transit ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700";
  return <span className={`inline-flex px-1.5 py-0.5 rounded-sm text-[10px] font-heading font-bold uppercase tracking-wider ${cls}`}>{status}</span>;
}

// ─────────────────────────────────────────────────────────────────────────────
function NewLogisticsDialog({ enums, onClose, onCreated }) {
  const [form, setForm] = useState({
    vin: "", status: "Encomendada", order_number: "", origin_country: "", origin_port: "",
    ship: "", container: "", carrier: "", departure_date: "", eta: "", destination_dealer: "",
    responsible_department: "Logística", next_action: "", deadline: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const upd = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([_, v]) => v !== ""));
      payload.vin = payload.vin.toUpperCase();
      await api.post("/vehicle-logistics", payload);
      onCreated();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Falha a criar processo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-md shadow-xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="new-logistics-dialog">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-5 py-3">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Novo processo logístico</div>
          <h2 className="font-display text-xl font-bold text-ronil-navy">Iniciar workflow de viatura</h2>
        </header>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <Field label="VIN *" full>
            <input required maxLength={17} minLength={17} value={form.vin} onChange={(e) => upd("vin", e.target.value.toUpperCase())} className={cls} placeholder="17 caracteres (sem I, O, Q)" data-testid="logistics-vin" />
          </Field>
          <Field label="Estado *">
            <select value={form.status} onChange={(e) => upd("status", e.target.value)} className={cls}>
              {enums.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Nº encomenda"><input value={form.order_number} onChange={(e) => upd("order_number", e.target.value)} className={cls} /></Field>
          <Field label="País de origem"><input value={form.origin_country} onChange={(e) => upd("origin_country", e.target.value)} className={cls} /></Field>
          <Field label="Porto de origem"><input value={form.origin_port} onChange={(e) => upd("origin_port", e.target.value)} className={cls} /></Field>
          <Field label="Navio"><input value={form.ship} onChange={(e) => upd("ship", e.target.value)} className={cls} /></Field>
          <Field label="Contentor"><input value={form.container} onChange={(e) => upd("container", e.target.value)} className={cls} /></Field>
          <Field label="Transportadora"><input value={form.carrier} onChange={(e) => upd("carrier", e.target.value)} className={cls} /></Field>
          <Field label="Data embarque"><input type="date" value={form.departure_date} onChange={(e) => upd("departure_date", e.target.value)} className={cls} /></Field>
          <Field label="ETA"><input type="date" value={form.eta} onChange={(e) => upd("eta", e.target.value)} className={cls} /></Field>
          <Field label="Dealer destino"><input value={form.destination_dealer} onChange={(e) => upd("destination_dealer", e.target.value)} className={cls} /></Field>
          <Field label="Departamento responsável">
            <select value={form.responsible_department} onChange={(e) => upd("responsible_department", e.target.value)} className={cls}>
              {enums.departments.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Próxima acção" full><input value={form.next_action} onChange={(e) => upd("next_action", e.target.value)} className={cls} /></Field>
          <Field label="Data limite"><input type="date" value={form.deadline} onChange={(e) => upd("deadline", e.target.value)} className={cls} /></Field>
          <Field label="Observações" full><textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className={cls} /></Field>
        </div>
        {error && <div className="mx-5 mb-3 text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
        <footer className="sticky bottom-0 bg-white border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">Cancelar</button>
          <button type="submit" disabled={busy || !form.vin || form.vin.length !== 17} className="px-4 py-2 text-sm bg-ronil-navy text-white rounded-md hover:bg-ronil-navy-hover disabled:opacity-50" data-testid="logistics-submit">
            {busy ? "A guardar…" : "Criar processo"}
          </button>
        </footer>
      </form>
    </div>
  );
}

const cls = "w-full border border-slate-200 rounded-md px-2.5 py-1.5 text-sm focus:border-ronil-navy focus:outline-none focus:ring-2 focus:ring-ronil-navy/10";

function Field({ label, children, full = false }) {
  return (
    <label className={`flex flex-col gap-1 ${full ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-slate-500">{label}</span>
      {children}
    </label>
  );
}
